import React, { useEffect } from 'react'
import { Route, Routes, useNavigate, useSearchParams } from 'react-router-dom'
import { Loader, Header,Text, Button } from '@pet-notify/ui'

import useDocumentTitle from 'hooks/document-title'
import ChoosePlanForm from './ChoosePlanForm'
import PaymentForm from './PaymentForm'
import Confirmation from './Confirmation'
import AccountForm from './AccountForm'
import { PlanInterval } from 'types/plan'
import { ActivateInput, ActivateInputUser } from 'services/orders'
import { usePersistedState } from 'hooks/persisted-state'
import { useOrderByActivationCode } from 'hooks/orders'
import style from './style.module.scss'
import Modal from 'components/Modal'
import HeaderOne from 'components/HeaderOne'

const LOCAL_STORAGE_KEY = 'activate-state'
type FormData = Partial<ActivateInput>

function ActivateV2Page() {
  useDocumentTitle('Intellitage Activation | Pet Notify')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const orderQuery = useOrderByActivationCode(
    searchParams.get('activationCode') || undefined,
  )
  const [state, setState] = usePersistedState<FormData>(
    {
      activationCode: searchParams.get('activationCode') || undefined,
      planInterval: PlanInterval.Year,
    },
    LOCAL_STORAGE_KEY,
  )

  useEffect(() => {
    if (orderQuery.data) {
      const { shippingAddress, user, activationCode } = orderQuery.data
      const { firstName, lastName, email } = shippingAddress

      setState((s) => ({
        ...s,
        firstName: firstName || user?.firstName,
        lastName: lastName || user?.lastName,
        email: email || user?.email,
        activationCode,
      }))
    }
  }, [orderQuery.data])

  const handleClose = ()=> {
    navigate('*')
  }

  const handleConfirmationSubmit = () => {
    navigate('payment')
  }

  const handleAccountSubmit = (
    data: ActivateInputUser & { activationCode: string },
  ) => {
    setState((s) => ({ ...s, ...data }))
    navigate('plan')
  }

  const handleChoosePlanSubmit = (
    data: Pick<ActivateInput, 'planInterval'>,
  ) => {
    setState((s) => ({ ...s, ...data }))
    navigate('confirmation')
  }

  const handlePaymentSubmit = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY)
    window.location.replace('/?activated=true')
  }

  if (orderQuery.isLoading && searchParams.get('activationCode')) {
    return <Loader />
  }

  return (
    <>
    <Routes>
      <Route
        path='plan'
        element={
          <ChoosePlanForm state={state} onSubmit={handleChoosePlanSubmit} />
        }
      />
      <Route
        path='confirmation'
        element={
          <Confirmation state={state} onSubmit={handleConfirmationSubmit} />
        }
      />
      <Route
        path='payment'
        element={<PaymentForm state={state} onSubmit={handlePaymentSubmit} />}
      />
      <Route
        path='*'
        element={<AccountForm onSubmit={handleAccountSubmit} state={state} />}
      />
    </Routes>
    <Routes>
    <Route
        path='free-account'
        element={
        <Modal onClose={handleClose}>
        <>
          <Header className={style.modalHeader}>Create free account</Header>
          <Text className={style.modalText}>
          Pet Notify Free Plan does not include Intellitag pet monitoring and emergency alarm notifications when you or your pet(s) need help.
          </Text>
          <Button
            className={style.modalConfirmButton}
            onClick={() => {
              window.location.href = '/signup/free'; // Replace with the full URL if needed, e.g., 'https://yourdomain.com/signup/free'
            }}
          >
            Continue to Free Plan
          </Button>
          <Button onClick={handleClose} variant='hollow'>
            Back to Intellitag Activation
          </Button>
        </>
      </Modal>
      }
      />
      <Route
        path='premium-plan'
        element={
        <Modal onClose={handleClose}>
          <>
            <div>
            <HeaderOne>Premium features include</HeaderOne>
              <ul className={style.features}>
                <li>Intellitag lost pet protection plan</li>
                <li>Engraved Intellitags for every household pet</li>
                <li>Intellitag lifetime replacement if lost, worn, or damanged</li>
                <li>Pet Vault unlimited document storage</li>
                <li>Automatic location based & daily check-in alarms</li>
                <li>SOS emergency alarm notifications</li>
                <li>Pet care task assignment to family & care providers</li>
                <li>Unlimited emergency & pet care contacts</li>
              </ul>
            </div>
          </>
        </Modal>}
      />
    </Routes>
    </>
  )
}

export default ActivateV2Page
